import _ from "lodash";
import React, { useMemo, useRef } from "react";
import { useMedia } from "react-media";
import { Menu, Ref, Image, Grid, Segment, Divider, Button, Popup } from "semantic-ui-react";
import { useSelector } from "react-redux";
import { useParams, Link } from "react-router-dom";
import { useClient } from "../hooks/use-client";
import { ConfirmationHeader } from "./ConfirmationHeader";
import identitySelectors from "../selectors/identity";
import "./PublicWrapper.scss";
import { ContactAvatar } from "../features/permits/components/ContactAvatar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare } from "@fortawesome/pro-regular-svg-icons";
import { FocusScope } from "react-aria";

interface IPublicWrapper {
  homeUrl: string;
  children: any;
  confirmation?: string;
  contentRef?: any;
  accountPage?: boolean;
}

export const PublicWrapper = ({ children, confirmation, homeUrl, contentRef, accountPage }: IPublicWrapper) => {
  const { site, id } = useParams<{ site: string; id: string }>();
  const firstName = useSelector(identitySelectors.getUserFirstName);
  const lastName = useSelector(identitySelectors.getUserLastName);
  const email = useSelector(identitySelectors.getUserEmail);
  const isSmallScreen = useMedia({ query: "(max-width: 640px)" });
  const isLoggedIn = useSelector(identitySelectors.isLoggedIn);
  const year = useMemo(() => new Date().getFullYear(), []);
  const client = useClient(site);
  const cityName = useMemo(() => _.get(client.query, "data.payload.data.attributes.name"), [client.query.data]);
  const citySeal = useMemo(() => _.get(client.query, "data.payload.data.attributes.seal"), [client.query.data]);
  const currentWindow = window.location.pathname;
  const applyTab = `/${site}/public/permits`;
  const permitsFirstPage = `/${site}/public/permits/applications/${id}`;
  const activeDashboardTab = currentWindow.includes("list") || currentWindow.includes("details");
  const activeApplyTab =
    currentWindow == applyTab || currentWindow == permitsFirstPage || currentWindow.includes("apply");

  const dialogRef = useRef<HTMLDialogElement>(null);

  // Close the modal if the click is outside the dialog content
  const handleClickOutside = (event: React.MouseEvent<HTMLElement>) => {
    if (dialogRef.current && event.target === dialogRef.current) {
      dialogRef.current?.close();
    }
  };

  return (
    <div className='public-wrapper'>
      {/* Disabling lint error to allow the modal background to be clicked */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <dialog
        ref={dialogRef}
        onClose={() => dialogRef.current?.close()}
        className='small-screen-nav'
        onClick={handleClickOutside}
      >
        <Segment basic>
          <Button
            icon='close'
            size='large'
            onClick={() => dialogRef.current?.close()}
            className='icon-only-button'
            floated='right'
            aria-label='Close dialog'
          />
          <Menu vertical fluid secondary className='tw-mt-6'>
            {!accountPage && isLoggedIn && (
              <>
                <Menu.Item
                  as={Link}
                  to={{
                    pathname: `/${site}/public/permits`,
                  }}
                  active={activeApplyTab}
                >
                  Apply
                </Menu.Item>
                <Menu.Item
                  as={Link}
                  to={{
                    pathname: `/${site}/public/permits/list`,
                  }}
                  active={activeDashboardTab}
                >
                  My Dashboard
                </Menu.Item>
              </>
            )}
            {isLoggedIn && (
              <>
                <Menu.Item
                  as={Link}
                  to={{
                    pathname: `/${site}/public/account`,
                  }}
                >
                  My Account
                </Menu.Item>
                <Menu.Item
                  as={Link}
                  to={{
                    pathname: `/${site}/logout`,
                  }}
                >
                  Logout
                </Menu.Item>
              </>
            )}
            {!isLoggedIn && (
              <>
                <Menu.Item
                  as={Link}
                  to={{
                    pathname: `/${site}/public/login`,
                    search: `?redirect=${homeUrl}`,
                  }}
                >
                  Sign In
                </Menu.Item>
                <Menu.Item
                  as={Link}
                  to={{
                    pathname: `/${site}/public/register`,
                    search: `?redirect=${homeUrl}`,
                  }}
                >
                  Sign Up
                </Menu.Item>
              </>
            )}
          </Menu>
        </Segment>
      </dialog>
      <a href='#main' className='skip-link'>
        Skip to Main Content
      </a>
      <header className='publicHeader'>
        <Grid centered>
          <Grid.Column computer={12} mobile={16} style={{ padding: 0, margin: 0 }}>
            <Menu secondary borderless>
              <Menu.Item
                header
                as={Link}
                to={{
                  pathname: `/${site}/public/permits`,
                }}
              >
                <div className='tw-flex tw-items-center tw-gap-3 tw-font-normal'>
                  <Image src={citySeal} alt='City Seal' style={{ width: 50, height: 50 }} />
                  <span className='header-3' style={{ maxWidth: "40vw", wordWrap: "break-word" }}>
                    {cityName}
                  </span>
                </div>
              </Menu.Item>
              <Menu.Menu position='right' className='dashboard-tabs'>
                {isLoggedIn && !isSmallScreen && (
                  <>
                    {!accountPage && (
                      <>
                        <Menu.Item
                          as={Link}
                          to={{
                            pathname: `/${site}/public/permits`,
                          }}
                          active={activeApplyTab}
                        >
                          Apply
                        </Menu.Item>
                        <Menu.Item
                          as={Link}
                          to={{
                            pathname: `/${site}/public/permits/list`,
                          }}
                          active={activeDashboardTab}
                        >
                          My Dashboard
                        </Menu.Item>
                      </>
                    )}
                    <Popup
                      trigger={
                        <button
                          aria-controls='account-menu'
                          aria-haspopup='dialog'
                          type='button'
                          className='profile-trigger tw-ml-4'
                        >
                          <ContactAvatar
                            firstName={firstName}
                            lastName={lastName}
                            size='40px'
                            ariaLabelPrefix='Account - '
                          />
                        </button>
                      }
                      position='bottom right'
                      size='tiny'
                      on='click'
                      className='profile-popup'
                      content={
                        <div id='account-menu' role='dialog'>
                          <Segment basic className='dropdown-text tw-p-4'>
                            <div aria-hidden='true'>
                              <ContactAvatar firstName={firstName} lastName={lastName} size='40px' />
                            </div>
                            <div>
                              <p className='name bold'> {`${firstName} ${lastName}`} </p>
                              <p className='email'> {email} </p>
                            </div>
                          </Segment>
                          <FocusScope contain restoreFocus autoFocus>
                            <div className='tw-mt-2 tw-text-lg'>
                              {!accountPage && (
                                <div className='profile-popup-item'>
                                  <Link tabIndex={0} role='button' target='_blank' to={`/${site}/public/account`}>
                                    My Account
                                    <FontAwesomeIcon
                                      icon={faArrowUpRightFromSquare}
                                      aria-label='Open new tab'
                                      className='tw-mr-2'
                                      size='sm'
                                      title='Open new tab'
                                    />
                                  </Link>
                                </div>
                              )}
                              <Divider fitted />
                              <div className='profile-popup-item'>
                                <Link to={`/${site}/logout`} role='button' tabIndex={0}>
                                  Logout
                                </Link>
                              </div>
                            </div>
                          </FocusScope>
                        </div>
                      }
                    />
                  </>
                )}
                {!isLoggedIn && !isSmallScreen && (
                  <>
                    <Menu.Item
                      as={Link}
                      fluid
                      style={{ fontSize: 18 }}
                      basic
                      to={{
                        pathname: `/${site}/public/login`,
                        search: `?redirect=${homeUrl}`,
                      }}
                    >
                      Sign In
                    </Menu.Item>
                    <Menu.Item
                      as={Link}
                      className='noHover'
                      fluid
                      basic
                      to={{
                        pathname: `/${site}/public/register`,
                        search: `?redirect=${homeUrl}`,
                      }}
                    >
                      <Button primary className='permitsButton'>
                        Sign Up
                      </Button>
                    </Menu.Item>
                  </>
                )}
                {isSmallScreen && (
                  <Button
                    icon='bars'
                    aria-label='Menu'
                    size='massive'
                    className='icon-only-button tw-mr-8'
                    onClick={() => dialogRef.current?.showModal()}
                    aria-haspopup='dialog'
                  />
                )}
              </Menu.Menu>
            </Menu>
          </Grid.Column>
        </Grid>
      </header>
      {confirmation && <ConfirmationHeader message={confirmation} />}
      <Segment
        basic
        style={{ padding: 0, margin: 0, flexGrow: 1, flexShrink: 1, overflowY: "auto", backgroundColor: "#F0F2F5" }}
      >
        <Grid centered style={{ padding: 0, margin: 0, minHeight: "calc(100vh - 114px)" }}>
          <Grid.Column
            computer={12}
            mobile={16}
            style={{ padding: 0, margin: 0, maxWidth: 1200 }}
            role='main'
            id='main'
          >
            <Ref innerRef={contentRef}>{children}</Ref>
          </Grid.Column>
        </Grid>
        <Segment basic style={{ flexShrink: 0, textAlign: "center" }}>
          <Divider />
          <p>Copyright © {year} GOGov</p>
        </Segment>
      </Segment>
    </div>
  );
};
